import type { ViewState } from '../types';
import type { Clave } from './textos';
import type { Traducir } from './idioma';

// ============================================================
//  El nombre de cada pantalla para la barra superior del móvil.
//
//  Esa barra pintaba `{currentView}` tal cual, y `currentView` es el
//  IDENTIFICADOR interno de la vista, no un texto para leer. En un teléfono
//  —donde no hay menú lateral y esa barra es el único sitio que dice dónde
//  estás— salía así, con la app entera en español:
//
//      Clients · Estimates · Pricebook · Workforce · FollowUps
//
//  «FollowUps» ni siquiera es una palabra: es el nombre de la ruta, junto y
//  con mayúscula en medio. Las doce pantallas estaban igual.
//
//  El menú lateral SÍ lo hacía bien —cada entrada lleva su `t('nav.…')`—,
//  pero esos rótulos se construyen dentro del componente del menú y en dos
//  ramas distintas según el oficio y el rol, así que la barra del móvil no
//  podía alcanzarlos. Este mapa es el sitio común que faltaba.
//
//  Va tipado como `Record<ViewState, …>` COMPLETO, no `Partial`: añadir una
//  vista nueva sin darle nombre deja de compilar. Ese es el punto — el fallo
//  original fue exactamente eso, una pantalla que nadie bautizó, y en móvil
//  no se nota hasta que alguien la abre en un teléfono.
//
//  `null` significa «esta vista no lleva barra superior»: el visor de
//  propuestas, el laboratorio de diseño y el asistente ocupan la pantalla
//  entera y App.tsx ni la dibuja.
// ============================================================

export const TITULO_DE_VISTA: Record<ViewState, Clave | null> = {
    Dashboard: 'nav.panel',
    Leads: 'clientes.tab.prospectos',
    Clients: 'nav.clientes',
    Estimates: 'nav.estimados',
    Projects: 'nav.proyectos',
    Finance: 'nav.finanzas',
    Calendar: 'nav.agenda',
    Pricebook: 'nav.precios',
    /* Templates no es una pantalla aparte: /templates redirige a
       /materials?section=templates, así que se llama como Precios. */
    Templates: 'nav.precios',
    ProposalViewer: null,
    Settings: 'nav.ajustes',
    DesignShowcase: null,
    FollowUps: 'nav.seguimientos',
    TimeClock: 'nav.reloj',
    Services: 'nav.rutas',
    Workforce: 'nav.gente',
    Inventory: 'nav.inventario',
    Maintenance: 'equipo.tab.mantenimiento',
    Classes: 'nav.clases',
    AIAssistant: null,
};

/**
 * El nombre de la pantalla en el idioma activo, o cadena vacía si no lleva.
 *
 * Recibe `t` en vez de llamar al hook porque esto no es un componente: así
 * se puede probar sin montar nada y sirve igual desde cualquier sitio.
 */
export function tituloDeVista(vista: ViewState, t: Traducir): string {
    const clave = TITULO_DE_VISTA[vista];
    return clave ? t(clave) : '';
}
